import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table, Button, Modal, Form, Input, message, Space, Popconfirm, App, Select, Tag, Card, Switch } from "antd";
import { EditOutlined, DeleteOutlined, UserAddOutlined, LockOutlined, UserSwitchOutlined } from "@ant-design/icons";
import { userManagementService } from "../../api/enhancedApi";
import { formatDate } from "../../utils/dateUtils";
import { useAuth } from "../../hooks/useAuth";
import useQuickSearch from "../../hooks/useQuickSearch";
import PermissionGuard from "../../components/auth/PermissionGuard";
import CollapsibleFilterBar from "../../components/common/CollapsibleFilterBar";
import UserListFilter from "./components/UserListFilter";

const ACCOUNT_TYPE_OPTIONS = [
    { value: "human", label: "普通用户", color: "green", description: "自然人交互登录账号" },
    { value: "agent", label: "Agent 账号", color: "geekblue", description: "自动化 Agent 主体" },
];

const LEGACY_ACCOUNT_TYPE_OPTIONS = {
    service: { label: "Legacy Service", color: "default" },
    system: { label: "Legacy System", color: "default" },
};

function accountTypeOption(accountType) {
    return ACCOUNT_TYPE_OPTIONS.find((item) => item.value === accountType) || LEGACY_ACCOUNT_TYPE_OPTIONS[accountType] || { label: "Unknown", color: "default" };
}

function renderAccountTypeTag(accountType) {
    const option = accountTypeOption(accountType || "human");
    return <Tag color={option.color}>{option.label}</Tag>;
}

const UserList = () => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [rolesModalVisible, setRolesModalVisible] = useState(false);
    const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [form] = Form.useForm();
    const [rolesForm] = Form.useForm();
    const [resetPasswordForm] = Form.useForm();
    const [editMode, setEditMode] = useState(false);
    const [filterParams, setFilterParams] = useState({});
    const { message: antdMessage } = App.useApp();
    const { hasPermission } = useAuth();

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 30,
        total: 0,
    });

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const userParams = {
                page: pagination.current,
                page_size: pagination.pageSize,
            };
            if (filterParams.search) userParams.search = filterParams.search;
            if (filterParams.account_type) userParams.account_type = filterParams.account_type;
            if (filterParams.is_active === true || filterParams.is_active === false) userParams.is_active = filterParams.is_active;
            if (filterParams.role_id) userParams.role_id = filterParams.role_id;
            if (filterParams.include_deleted) userParams.include_deleted = true;

            const usersResponse = await userManagementService.fetchUsers(userParams);
            if (usersResponse && Array.isArray(usersResponse.items) && typeof usersResponse.total === "number") {
                setUsers(usersResponse.items);
                setPagination((prev) => ({ ...prev, total: usersResponse.total }));
            } else {
                antdMessage.error("获取用户数据格式不正确");
                console.error("Unexpected usersData format:", usersResponse);
                setUsers([]);
                setPagination((prev) => ({ ...prev, total: 0 }));
            }
        } catch (error) {
            antdMessage.error("获取数据失败: " + (error.message || "未知错误"));
            console.error(error);
            setUsers([]);
            setPagination((prev) => ({ ...prev, total: 0 }));
        } finally {
            setLoading(false);
        }
    }, [filterParams, pagination.current, pagination.pageSize, antdMessage]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        let cancelled = false;
        const loadRoles = async () => {
            try {
                const rolesResponse = await userManagementService.fetchRoles({ page: 1, page_size: 200 });
                const items = Array.isArray(rolesResponse?.items) ? rolesResponse.items : Array.isArray(rolesResponse) ? rolesResponse : null;
                if (cancelled) return;
                if (items) {
                    setRoles(items);
                } else {
                    antdMessage.error("获取角色列表格式不正确");
                    setRoles([]);
                }
            } catch (error) {
                if (!cancelled) {
                    antdMessage.error("获取角色列表失败");
                    console.error(error);
                    setRoles([]);
                }
            }
        };
        loadRoles();
        return () => {
            cancelled = true;
        };
    }, [antdMessage]);

    const handleFilterSearch = useCallback((values) => {
        setFilterParams((prev) => {
            const next = {};
            if (prev.search) next.search = prev.search;
            if (values.account_type) next.account_type = values.account_type;
            if (values.is_active === "true" || values.is_active === "false") next.is_active = values.is_active === "true";
            if (values.role_id) next.role_id = values.role_id;
            if (values.include_deleted) next.include_deleted = true;
            return next;
        });
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const quickSearch = useQuickSearch({ setFilterParams, setPagination });

    const handleFilterReset = useCallback(() => {
        quickSearch.clear();
        setFilterParams({});
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [quickSearch]);

    const filterSummaryItems = useMemo(() => {
        const items = [];
        if (filterParams.account_type) {
            const option = ACCOUNT_TYPE_OPTIONS.find((item) => item.value === filterParams.account_type);
            items.push({ label: "账号类型", value: option?.label || filterParams.account_type });
        }
        if (filterParams.is_active === true || filterParams.is_active === false) {
            items.push({ label: "状态", value: filterParams.is_active ? "启用" : "禁用" });
        }
        if (filterParams.role_id) {
            const role = roles.find((item) => item.id === filterParams.role_id);
            items.push({ label: "角色", value: role?.name || String(filterParams.role_id) });
        }
        if (filterParams.include_deleted) {
            items.push({ label: "已归档", value: "包含" });
        }
        return items;
    }, [filterParams, roles]);

    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));
    };

    const handleAdd = () => {
        setEditMode(false);
        setSelectedUser(null);
        form.resetFields();
        form.setFieldsValue({ is_active: true });
        setFormVisible(true);
    };

    const handleEdit = (user) => {
        setEditMode(true);
        setSelectedUser(user);
        form.setFieldsValue({
            ...user,
            role_ids: user.roles ? user.roles.map((role) => role.id) : [],
        });
        setFormVisible(true);
    };

    const handleDelete = async (userId) => {
        if (!hasPermission("user:delete")) {
            message.error("您没有删除用户的权限");
            return;
        }

        try {
            await userManagementService.deleteUser(userId);
            message.success("用户已归档");
            fetchUsers();
        } catch (error) {
            console.error("归档用户错误:", error);
            message.error("归档用户失败");
        }
    };

    const handleFormSubmit = async () => {
        try {
            const values = await form.validateFields();
            console.log("表单验证通过:", values);

            if (editMode) {
                if (!hasPermission("user:update")) {
                    message.error("您没有更新用户的权限");
                    return;
                }

                const updateData = {
                    email: values.email,
                    full_name: values.full_name || "",
                    is_active: values.is_active,
                    is_superuser: values.is_superuser,
                };

                console.log("更新用户数据:", updateData);
                await userManagementService.updateUser(selectedUser.id, updateData);
                message.success("用户已更新");
            } else {
                if (!hasPermission("user:create")) {
                    message.error("您没有创建用户的权限");
                    return;
                }

                const createData = {
                    username: values.username,
                    password: values.password,
                    email: values.email,
                    full_name: values.full_name || "",
                    is_active: values.is_active === undefined ? true : values.is_active,
                    is_superuser: values.is_superuser === undefined ? false : values.is_superuser,
                    role_ids: values.role_ids || [],
                };

                if (!/[A-Z]/.test(createData.password)) {
                    message.error("密码必须包含至少一个大写字母");
                    return;
                }
                if (!/[a-z]/.test(createData.password)) {
                    message.error("密码必须包含至少一个小写字母");
                    return;
                }
                if (!/\d/.test(createData.password)) {
                    message.error("密码必须包含至少一个数字");
                    return;
                }
                if (createData.password.length < 8) {
                    message.error("密码长度必须至少为8个字符");
                    return;
                }

                console.log("创建用户数据:", createData);
                await userManagementService.createUser(createData);
                message.success("用户已创建，已自动归入待分配组织");
            }

            setFormVisible(false);
            fetchUsers();
        } catch (error) {
            console.error("表单提交错误:", error);

            if (error.response) {
                const errorData = error.response.data;
                console.error("错误响应详情:", errorData);

                if (errorData.detail) {
                    message.error(`提交失败: ${errorData.detail}`);
                } else if (typeof errorData === "object") {
                    const errorMessages = [];
                    for (const key in errorData) {
                        if (Array.isArray(errorData[key])) {
                            errorData[key].forEach((msg) => errorMessages.push(`${key}: ${msg}`));
                        } else {
                            errorMessages.push(`${key}: ${errorData[key]}`);
                        }
                    }

                    if (errorMessages.length > 0) {
                        message.error(`提交失败: ${errorMessages.join(", ")}`);
                    } else {
                        message.error("提交失败，请检查输入");
                    }
                } else {
                    message.error("提交失败，请检查输入");
                }
            } else {
                message.error("提交失败，请检查输入");
            }
        }
    };

    const handleManageRoles = (user) => {
        if (!hasPermission("role:update")) {
            message.error("您没有分配角色的权限");
            return;
        }

        setSelectedUser(user);
        rolesForm.setFieldsValue({
            role_ids: user.roles ? user.roles.map((role) => role.id) : [],
        });
        setRolesModalVisible(true);
    };

    const handleResetPassword = (user) => {
        if (!hasPermission("user:update")) {
            message.error("您没有重置密码的权限");
            return;
        }

        setSelectedUser(user);
        resetPasswordForm.resetFields();
        setResetPasswordVisible(true);
    };

    const handleRolesSubmit = async () => {
        if (!hasPermission("role:update")) {
            message.error("您没有分配角色的权限");
            return;
        }

        const values = await rolesForm.validateFields();
        await userManagementService.assignUserRoles(selectedUser.id, values);
        message.success("角色已分配");
        setRolesModalVisible(false);
        fetchUsers();
    };

    const handleResetPasswordSubmit = async () => {
        if (!hasPermission("user:update")) {
            message.error("您没有重置密码的权限");
            return;
        }

        const values = await resetPasswordForm.validateFields();

        if (!/[A-Z]/.test(values.password)) {
            message.error("密码必须包含至少一个大写字母");
            return;
        }
        if (!/[a-z]/.test(values.password)) {
            message.error("密码必须包含至少一个小写字母");
            return;
        }
        if (!/\d/.test(values.password)) {
            message.error("密码必须包含至少一个数字");
            return;
        }
        if (values.password.length < 8) {
            message.error("密码长度必须至少为8个字符");
            return;
        }

        await userManagementService.resetUserPassword(selectedUser.id, values.password);
        message.success("密码已重置");
        setResetPasswordVisible(false);
    };

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 60,
        },
        {
            title: "用户名",
            dataIndex: "username",
            key: "username",
            width: 120,
        },
        {
            title: "邮箱",
            dataIndex: "email",
            key: "email",
            width: 200,
        },
        {
            title: "姓名",
            dataIndex: "full_name",
            key: "full_name",
            width: 120,
            render: (text) => text || "-",
        },
        {
            title: "账号类型",
            dataIndex: "account_type",
            key: "account_type",
            width: 120,
            render: renderAccountTypeTag,
        },
        {
            title: "状态",
            dataIndex: "is_active",
            key: "is_active",
            width: 100,
            render: (active) => (active ? <Tag color="green">启用</Tag> : <Tag color="red">禁用</Tag>),
        },
        {
            title: "角色",
            dataIndex: "roles",
            key: "roles",
            width: 200,
            render: (roles) => (
                <div>
                    {roles && roles.length > 0 ? (
                        roles.map((role) => (
                            <Tag color="blue" key={role.id}>
                                {role.name}
                            </Tag>
                        ))
                    ) : (
                        <span>-</span>
                    )}
                </div>
            ),
        },
        {
            title: "最后登录",
            dataIndex: "last_login",
            key: "last_login",
            width: 170,
            render: (date) => (date ? formatDate(date) : "-"),
        },
        {
            title: "创建时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 170,
            render: (date) => formatDate(date),
        },
        {
            title: "操作",
            key: "action",
            width: 280,
            render: (_, record) => (
                <Space size="small">
                    <PermissionGuard permission="user:update">
                        <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
                            编辑
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="role:update">
                        <Button icon={<UserSwitchOutlined />} size="small" onClick={() => handleManageRoles(record)}>
                            角色
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="user:update">
                        <Button icon={<LockOutlined />} size="small" onClick={() => handleResetPassword(record)}>
                            重置密码
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="user:delete">
                        <Popconfirm title="确定要删除此用户吗?" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
                            <Button danger icon={<DeleteOutlined />} size="small">
                                删除
                            </Button>
                        </Popconfirm>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <div className="page-header-compact">
                <div className="page-title-compact">用户管理</div>
                <div className="page-action-compact">
                    <PermissionGuard permission="user:create">
                        <Button type="primary" icon={<UserAddOutlined />} onClick={handleAdd}>
                            添加用户
                        </Button>
                    </PermissionGuard>
                </div>
            </div>

            <CollapsibleFilterBar
                summaryItems={filterSummaryItems}
                onReset={handleFilterReset}
                drawerTitle="用户管理 - 筛选条件"
                quickControls={<Input placeholder="搜索用户名 / 邮箱 / 姓名" style={{ width: 260, height: 26 }} {...quickSearch.inputProps} />}
            >
                <UserListFilter
                    onSearch={handleFilterSearch}
                    roles={roles}
                    accountTypeOptions={ACCOUNT_TYPE_OPTIONS.map((item) => ({ label: item.label, value: item.value }))}
                    autoApply
                />
            </CollapsibleFilterBar>

            <Card className="data-card">
                <Table
                    dataSource={users}
                    columns={columns}
                    rowKey="id"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    loading={loading}
                    size="small"
                    scroll={{ x: 1300 }}
                    onChange={handleTableChange}
                />
            </Card>

            <Modal title={editMode ? "编辑用户" : "添加用户"} open={formVisible} onOk={handleFormSubmit} onCancel={() => setFormVisible(false)} maskClosable={false} okText="保存" cancelText="取消" width={550}>
                <Form form={form} layout="vertical" requiredMark>
                    <Form.Item name="username" label="用户名" rules={[{ required: true, message: "请输入用户名" }]} disabled={editMode}>
                        <Input disabled={editMode} placeholder="请输入用户名" />
                    </Form.Item>

                    {!editMode && (
                        <Form.Item name="password" label="密码" rules={[{ required: true, message: "请输入密码" }]}>
                            <Input.Password placeholder="请输入密码，至少8位，包含大小写字母和数字" />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="email"
                        label="邮箱"
                        rules={[
                            { required: true, message: "请输入邮箱" },
                            { type: "email", message: "请输入有效的邮箱地址" },
                        ]}
                    >
                        <Input placeholder="请输入邮箱" />
                    </Form.Item>

                    <Form.Item name="full_name" label="姓名">
                        <Input placeholder="请输入姓名" />
                    </Form.Item>

                    <Form.Item label="账号类型">
                        <Space wrap>
                            {renderAccountTypeTag(editMode ? selectedUser?.account_type : "human")}
                            <span style={{ color: "#8c8c8c" }}>
                                {editMode ? "身份边界字段，只读展示。" : "管理员创建入口只创建普通人工用户。"}
                            </span>
                        </Space>
                    </Form.Item>

                    <Form.Item name="is_active" label="状态" valuePropName="checked" initialValue={true}>
                        <Switch checkedChildren="启用" unCheckedChildren="禁用" />
                    </Form.Item>

                    <Form.Item name="is_superuser" label="超级管理员" valuePropName="checked" initialValue={false}>
                        <Switch checkedChildren="是" unCheckedChildren="否" />
                    </Form.Item>

                    {!editMode && (
                        <Form.Item name="role_ids" label="角色">
                            <Select mode="multiple" placeholder="请选择角色" optionFilterProp="label">
                                {roles.map((role) => (
                                    <Select.Option key={role.id} value={role.id} label={role.name}>
                                        {role.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            <Modal title="分配角色" open={rolesModalVisible} onOk={handleRolesSubmit} onCancel={() => setRolesModalVisible(false)} maskClosable={false} okText="保存" cancelText="取消">
                <Form form={rolesForm} layout="vertical">
                    <Form.Item name="role_ids" label="选择角色">
                        <Select mode="multiple" placeholder="请选择角色" style={{ width: "100%" }} optionFilterProp="label">
                            {roles.map((role) => (
                                <Select.Option key={role.id} value={role.id} label={role.name}>
                                    {role.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal title="重置密码" open={resetPasswordVisible} onOk={handleResetPasswordSubmit} onCancel={() => setResetPasswordVisible(false)} maskClosable={false} okText="保存" cancelText="取消">
                <Form form={resetPasswordForm} layout="vertical">
                    <Form.Item name="password" label="新密码" rules={[{ required: true, message: "请输入新密码" }]}>
                        <Input.Password placeholder="请输入新密码，至少8位，包含大小写字母和数字" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default UserList;
